import React, { FC } from 'react'
import { PlayerProfileResponse, PlayerPosition as PlayerPositionEnum, PlayerStatus, Player } from 'protos'
import { Typography, Grid, makeStyles, Box } from '@material-ui/core'

import NavigationBackButton from 'components/NavigationBackButton'
import { PlayerStatusToken } from 'lib/enumTranslations'
import PlayerPosition from 'components/PlayerPosition'
import JerseyBack from 'components/JerseyBack'
import NavButton from 'components/NavButton'
import T from 'components/FormattedMessage'
import Point from 'components/Point'

import subOutIconUrl from './assets/sub_out.svg'

type Props = {
  profile: PlayerProfileResponse
  onBackClick?: () => void
  substitutionLink?: string
}

const useStyles = makeStyles(({ breakpoints, palette, spacing }) => {
  return {
    root: {
      minHeight: 196,
      minWidth: 320,
      display: 'flex',
      backgroundImage: 'linear-gradient(135deg, #D3DBE8, #ffffff)',
    },

    jerseyContainer: {
      marginRight: -60,
      marginLeft: 60,
      marginBottom: -100,

      [breakpoints.only('xs')]: {
        marginRight: '-35%',
        marginLeft: '20%',
        marginBottom: '-55%',
      },
    },

    subButtonContainer: {
      minHeight: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
    },

    subButton: {
      border: '1px solid #27354F63',
      backgroundColor: palette.common.white,
    },

    detailsContainer: {
      position: 'absolute',
      left: spacing(4),
      top: spacing(7),
      bottom: 0,
      right: 0,
    },

    playerStatusPositive: {
      color: '#06CD90',
    },

    playerStatusNegative: {
      color: palette.error.main,
    },

    leftHalf: {
      flex: 212,
      position: 'relative',
    },

    subIcon: {
      marginRight: spacing(1),
    },

    rightHalf: {
      flex: 163,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundImage: 'linear-gradient(45deg, #D3DBE8, #ffffff)',
    },

    backgroundNumber: {
      paddingTop: spacing(2),
      textAlign: 'center',
      fontWeight: 700,
      fontSize: 98,
      color: '#27354F',
      opacity: 0.03,
      userSelect: 'none',
    },
  }
})

const PlayerProfileHeader: FC<Props> = ({ profile, substitutionLink }) => {
  const classes = useStyles()

  if (!profile.player) {
    return null
  }

  const player = profile.player as Player

  return (
    <div className={classes.root}>
      <div className={classes.leftHalf}>
        <div className={classes.backgroundNumber}>
          {player.jersey?.jerseyNumber}
        </div>

        <NavigationBackButton />

        <div className={classes.detailsContainer}>
          <Typography variant="h6">
            {player.name}
          </Typography>

          <Typography variant="body1" color="textSecondary">
            {player.team?.name} (<PlayerPosition position={player.position} />)
          </Typography>

          <Box mt={2} mb={1}>
            <Grid container spacing={2}>
              <Grid item>
                <Point variant="cost" value={player.cost} />
              </Grid>
              <Grid item>
                <Point variant="score" value={profile.totalScore} />
              </Grid>
            </Grid>
          </Box>

          <Typography
            variant="body1"
            className={{
              [PlayerStatus.PS_UNKNOWN]: undefined,
              [PlayerStatus.PS_FINISHED]: undefined,
              [PlayerStatus.PS_ACTIVE]: classes.playerStatusPositive,
              [PlayerStatus.PS_INACTIVE]: classes.playerStatusNegative,
              [PlayerStatus.PS_LEFTOUT]: classes.playerStatusNegative,
              [PlayerStatus.PS_SENTOFF]: classes.playerStatusNegative,
            }[player.status]}
          >
            ● <T id={PlayerStatusToken(player.status)} />
          </Typography>
        </div>
      </div>

      <div className={classes.rightHalf}>
        <div className={classes.subButtonContainer}>
          {substitutionLink && (
            <NavButton
              replace
              to={substitutionLink}
              variant="contained"
              color="inherit"
              className={classes.subButton}
            >
              <img
                src={subOutIconUrl}
                alt="sub out"
                className={classes.subIcon}
              />
              <T id="teameditor_action_subout" />
            </NavButton>
          )}
        </div>

        <div className={classes.jerseyContainer}>
          <JerseyBack
            {...player.jersey}
            isGoalkeeper={player.position === PlayerPositionEnum.PP_GOALKEEPER}
          />
        </div>
      </div>
    </div>
  )
}

export default PlayerProfileHeader
try {
    // @ts-ignore
    PlayerProfileHeader.displayName = "PlayerProfileHeader";
    // @ts-ignore
    PlayerProfileHeader.__docgenInfo = { "description": "", "displayName": "PlayerProfileHeader", "props": { "profile": { "defaultValue": null, "description": "", "name": "profile", "required": true, "type": { "name": "any" } }, "onBackClick": { "defaultValue": null, "description": "", "name": "onBackClick", "required": false, "type": { "name": "() => void" } }, "substitutionLink": { "defaultValue": null, "description": "", "name": "substitutionLink", "required": false, "type": { "name": "string" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/components/PlayerLayer/components/PlayerProfile/components/PlayerProfileHeader/PlayerProfileHeader.tsx#PlayerProfileHeader"] = { docgenInfo: PlayerProfileHeader.__docgenInfo, name: "PlayerProfileHeader", path: "src/components/PlayerLayer/components/PlayerProfile/components/PlayerProfileHeader/PlayerProfileHeader.tsx#PlayerProfileHeader" };
}
catch (__react_docgen_typescript_loader_error) { }